import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight, FileText, Loader2, RefreshCw, AlertCircle } from "lucide-react";
import { Button } from "@ndd/ui";
import { authenticatedApi } from "@/lib/api";
import {
  SECTION_DISPLAY_NAMES,
  getNextIncompleteSection,
  type AnalysisResponse,
  type AnalysisStatus,
} from "../../../../shared/analysis-types";
import { formatLocalDate } from "../../../../shared/local-date";

function useAnalysisProgress(sessionId: string, status: AnalysisStatus) {
  const [progressSection, setProgressSection] = useState<string | null>(null);
  const [currentStatus, setCurrentStatus] = useState(status);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isActive = currentStatus === "pending" || currentStatus === "analyzing";

  useEffect(() => {
    if (!isActive) return;

    async function poll() {
      try {
        const res = await fetch(`/api/sessions/${sessionId}/analysis`, {
          credentials: "same-origin",
        });
        if (!res.ok) return;
        const { data } = (await res.json()) as { data: AnalysisResponse };

        if (data.status === "complete" || data.status === "error") {
          setCurrentStatus(data.status);
          if (intervalRef.current) clearInterval(intervalRef.current);
          return;
        }

        const next = getNextIncompleteSection(data.sections ?? {});
        setProgressSection(next ? SECTION_DISPLAY_NAMES[next] : null);
      } catch {
        // silently ignore polling errors
      }
    }

    void poll();
    intervalRef.current = setInterval(() => void poll(), 1500);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [sessionId, isActive]);

  const restartPolling = useCallback(() => {
    setCurrentStatus("pending");
    setProgressSection(null);
  }, []);

  return { progressSection: isActive ? progressSection : null, currentStatus, restartPolling };
}

export interface AnalysisPillProps {
  sessionId: string;
  sessionDate: string | null;
  clientName: string | null;
  summary: string | null;
  status: AnalysisStatus;
  errorMessage: string | null;
  onOpenScorecard: (sessionId: string) => void;
  onStatusChange?: (status: "complete" | "error") => void;
  onDismiss?: () => void;
}

function ResolvedPill({
  sessionDate,
  clientName,
  summary,
  onOpenScorecard,
}: {
  sessionDate: string | null;
  clientName: string | null;
  summary: string | null;
  onOpenScorecard: () => void;
}) {
  const date = formatLocalDate(sessionDate);

  return (
    <button
      type="button"
      onClick={onOpenScorecard}
      className="flex items-center gap-3 rounded-xl border border-sand-200 bg-card hover:bg-sand-50 px-4 py-3 max-w-[540px] w-full text-left transition-colors cursor-pointer"
    >
      <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-sand-100 shrink-0">
        <FileText className="h-4 w-4 text-sand-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-foreground">
          Session analysis
          {date && (
            <span className="font-normal text-sand-400">
              {" "}&middot; {date}
            </span>
          )}
          {clientName && (
            <span className="font-normal text-sand-400">
              {" "}&middot; {clientName}
            </span>
          )}
        </p>
        {summary && (
          <p className="text-sm text-sand-500 truncate mt-0.5">{summary}</p>
        )}
      </div>
      <ChevronRight className="h-4 w-4 text-sand-400 shrink-0" />
    </button>
  );
}

function AnalyzingPill({
  sessionDate,
  clientName,
  progressSection,
}: {
  sessionDate: string | null;
  clientName: string | null;
  progressSection: string | null;
}) {
  const date = formatLocalDate(sessionDate);

  return (
    <div className="flex items-center gap-3 rounded-xl border border-sand-200 bg-card px-4 py-3 max-w-[540px] w-full">
      <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-sand-100 shrink-0">
        <Loader2 className="h-4 w-4 text-interactive animate-spin" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-foreground">
          Analyzing new session
          {date && (
            <span className="font-normal text-sand-400">
              {" "}&middot; {date}
            </span>
          )}
          {clientName && (
            <span className="font-normal text-sand-400">
              {" "}&middot; {clientName}
            </span>
          )}
        </p>
        <p className="text-sm text-sand-500 mt-0.5">
          {progressSection
            ? `Working on ${progressSection}`
            : "Starting analysis..."}
        </p>
      </div>
    </div>
  );
}

function ErrorPill({
  sessionDate,
  clientName,
  errorMessage,
  sessionId,
  onRetry,
  onDismiss,
}: {
  sessionDate: string | null;
  clientName: string | null;
  errorMessage: string | null;
  sessionId: string;
  onRetry: () => void;
  onDismiss: () => void;
}) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const date = formatLocalDate(sessionDate);
  const [retrying, setRetrying] = useState(false);
  const displayError = errorMessage?.slice(0, 300) ?? null;

  const handleRetry = useCallback(async () => {
    setRetrying(true);
    try {
      await authenticatedApi(getToken, `/api/sessions/${sessionId}/retry`, {
        method: "POST",
      });
      onRetry();
      void queryClient.invalidateQueries({ queryKey: ["rd-sessions"] });
      void queryClient.invalidateQueries({ queryKey: ["rd"] });
    } catch {
      // parity with legacy: a failed retry keeps the error pill visible
    } finally {
      setRetrying(false);
    }
  }, [getToken, queryClient, sessionId, onRetry]);

  return (
    <div className="flex items-center gap-3 rounded-xl border border-error-light bg-error-light/40 px-4 py-3 max-w-[620px] w-full">
      <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-error-light shrink-0">
        <AlertCircle className="h-4 w-4 text-error" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-error">
          Analysis failed
          {date && <span> &middot; {date}</span>}
          {clientName && (
            <span className="font-normal text-sand-500">
              {" "}&middot; {clientName}
            </span>
          )}
        </p>
        {displayError && (
          <p className="text-sm text-sand-600 mt-0.5">{displayError}</p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          size="sm"
          onClick={() => void handleRetry()}
          disabled={retrying}
          className="bg-error hover:bg-error/90 text-white rounded-lg px-3 py-1.5 text-sm"
        >
          {retrying && <RefreshCw className="h-3 w-3 animate-spin mr-1" />}
          Retry analysis
        </Button>
        <button
          type="button"
          onClick={onDismiss}
          className="text-sm text-sand-500 hover:text-sand-600"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

export function AnalysisPill({
  sessionId,
  sessionDate,
  clientName,
  summary,
  status,
  errorMessage,
  onOpenScorecard,
  onStatusChange,
  onDismiss,
}: AnalysisPillProps) {
  const queryClient = useQueryClient();
  const { progressSection, currentStatus, restartPolling } = useAnalysisProgress(sessionId, status);
  const [dismissed, setDismissed] = useState(false);
  const initialStatusRef = useRef(status);
  const hasNotifiedRef = useRef(false);

  useEffect(() => {
    if (hasNotifiedRef.current) return;
    if (currentStatus === initialStatusRef.current) return;
    if (currentStatus === "complete" || currentStatus === "error") {
      hasNotifiedRef.current = true;
      onStatusChange?.(currentStatus);
      // The sessions list on the RD page reads from the ['rd-sessions', rdId]
      // query; invalidating pulls the new status/summary into the row
      // alongside the pill.
      void queryClient.invalidateQueries({ queryKey: ["rd-sessions"] });
      void queryClient.invalidateQueries({ queryKey: ["rd"] });
    }
  }, [currentStatus, onStatusChange, queryClient]);

  if (dismissed) return null;

  if (currentStatus === "complete") {
    return (
      <ResolvedPill
        sessionDate={sessionDate}
        clientName={clientName}
        summary={summary}
        onOpenScorecard={() => onOpenScorecard(sessionId)}
      />
    );
  }

  if (currentStatus === "error") {
    return (
      <ErrorPill
        sessionDate={sessionDate}
        clientName={clientName}
        errorMessage={errorMessage}
        sessionId={sessionId}
        onRetry={restartPolling}
        onDismiss={() => { setDismissed(true); onDismiss?.(); }}
      />
    );
  }

  return (
    <AnalyzingPill
      sessionDate={sessionDate}
      clientName={clientName}
      progressSection={progressSection}
    />
  );
}
